/**
 * DndSelector<T> — sélecteur drag-and-drop générique deux niveaux.
 *
 * Accepte un `config` typé décrivant comment mapper les données domaine vers la
 * structure DnD interne. Le consumer ne manipule aucun composant DnD directement.
 *
 * Modes :
 * - **Flat** (getChildren absent ou retourne []) : items atomiques sans sous-éléments
 * - **Hiérarchique** (getChildren retourne DndChild[]) : parents avec enfants draggables
 * - **Lecture seule** (`readOnly`) : consultation du contenu de la colonne cible, sans drag
 *   ni sélection. À combiner avec `columnConfig.numColumns: 1`. Les accordéons et la
 *   recherche par colonne restent actifs.
 *
 * Usage :
 * ```tsx
 * <DndSelector
 *   config={{
 *     data: clients,
 *     itemConfig: {
 *       getId: (c) => c.id,
 *       getLabel: (c) => c.oss_code,
 *       getChildren: (c) => c.contracts.map(ct => ({ id: ct.id, primaryLabel: ct.oss_code })),
 *     },
 *     columnConfig: { numColumns: 3, titles: { target: 'Autorisés', source: 'Clients' } },
 *     toolbarConfig: { title: 'Autorisations' },
 *   }}
 *   onChange={({ byColumn }) => console.log(byColumn)}
 * />
 * ```
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalContext } from '@applications-terrains/birdz-react-library';
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { Box, Divider } from '@mui/material';
import { DndColumn, DndParentEntry, DndSelectorProps, DropDisabledContext } from './types';
import {
  buildDndColumns,
  extractSelectorOutput,
  mapDataToParents,
  applyDndMultiDrag,
  applyDndSingleDrag
} from './const';
import { useAccordionState } from './hooks/useAccordionState';
import { useSelectionState } from './hooks/useSelectionState';
import { SelectionProvider } from './contexts/SelectionContext';
import DndItemCard from './components/DndItemCard';
import DndChildCard from './components/DndChildCard';
import { MultiDragOverlay } from './components/DndPrimitives';
import DndColumnBox from './components/DndColumnBox';
import DndToolbar from './components/DndToolbar';
import { FullSizeLoader } from '../Suspended';

type ActiveElement = {
  id: string;
  type: 'parent' | 'child';
  element: DndParentEntry;
  columnId: DndColumn['id'];
};

const DndSelector = <T,>({
  config,
  onChange,
  isLoading,
  readOnly = false,
  height = 600
}: DndSelectorProps<T>) => {
  const { data, itemConfig, columnConfig, initial, toolbarConfig } = config;
  const { bgColor } = useGlobalContext();
  const isDark = bgColor === 'dark-mode';

  const parents = useMemo(() => mapDataToParents(data, itemConfig), [data, itemConfig]);

  const [activeDrag, setActiveDrag] = useState<ActiveElement | null>(null);
  const { isOpen, hasOpen, transferOpenStates, toggle, closeAll } = useAccordionState();
  const { selection, isSelected, toggleSelect, clearSelection, parseKey, removeMany, addMany } =
    useSelectionState();

  const buildColumns = useCallback(
    () =>
      buildDndColumns({
        parents: mapDataToParents(data, itemConfig),
        authorisedParents: initial?.targetParents,
        excludedParents: initial?.excludedParents,
        authorisedChildren: initial?.targetChildren,
        excludedChildren: initial?.excludedChildren,
        numColumns: columnConfig.numColumns,
        titles: columnConfig.titles
      }),
    [data, itemConfig, initial, columnConfig.numColumns, columnConfig.titles]
  );

  const [initialColumns, setInitialColumns] = useState<DndColumn[]>(() => buildColumns());
  const [columns, setColumns] = useState<DndColumn[]>(initialColumns);

  const dataInitialized = useRef(data.length > 0);

  useEffect(() => {
    if (!dataInitialized.current && data.length > 0) {
      dataInitialized.current = true;
      const newCols = buildColumns();
      setInitialColumns(newCols);
      setColumns(newCols);
    }
  }, [data, buildColumns]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    onChangeRef.current?.(extractSelectorOutput(columns));
  }, [columns]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function handleDragEnd(event: DragEndEvent) {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) {
      clearSelection();
      return;
    }

    const activeData = active.data.current;
    if (!activeData) {
      clearSelection();
      return;
    }

    const { type, element, columnId: sourceColumnId } = activeData;
    const targetColumnId = over.id as string;

    if (sourceColumnId === targetColumnId) {
      clearSelection();
      return;
    }

    const activeId = type === 'parent' ? element.id : element.children[0]?.id;
    const isDraggingSelected = isSelected(sourceColumnId, type, activeId);

    if (selection.size > 1 && isDraggingSelected) {
      const itemsToTransfer: { itemCode: string; sourceColumnId: string }[] = [];
      selection.forEach((key) => {
        const { columnId: kCol, type: kType, itemId: kItemId } = parseKey(key);
        if (kType === 'parent' && kCol !== targetColumnId) {
          const srcCol = columns.find((col) => col.id === kCol);
          const entry = srcCol?.items.find((item) => item.id === kItemId);
          if (entry) itemsToTransfer.push({ itemCode: entry.label, sourceColumnId: kCol });
        }
      });
      transferOpenStates(itemsToTransfer, targetColumnId);
      setColumns((prev) => applyDndMultiDrag(prev, selection, targetColumnId, parents, parseKey));
    } else {
      transferOpenStates([{ itemCode: element.label, sourceColumnId }], targetColumnId);
      setColumns((prev) =>
        applyDndSingleDrag(prev, type, element, sourceColumnId, targetColumnId, parents)
      );
    }

    clearSelection();
  }

  const activeId = activeDrag
    ? activeDrag.type === 'parent'
      ? activeDrag.element.id
      : activeDrag.element.children[0]?.id
    : undefined;

  const isMultiDrag =
    !!activeDrag &&
    selection.size > 1 &&
    !!activeId &&
    isSelected(activeDrag.columnId ?? 'source', activeDrag.type, activeId);

  const onReset = () => {
    setColumns(initialColumns);
    clearSelection();
    closeAll();
  };

  const authorizedLength = columns.find((col) => col.id === 'target')?.items.length ?? 0;
  const unauthorizedLength = columns.find((col) => col.id === 'excluded')?.items.length ?? 0;

  const columnItemCounts = useMemo(
    () => Object.fromEntries(columns.map((col) => [col.id, col.items.length])),
    [columns]
  );

  const columnItemIds = useMemo(
    () => Object.fromEntries(columns.map((col) => [col.id, col.items.map((item) => item.id)])),
    [columns]
  );

  const buildDropDisabledContext = (): DropDisabledContext => ({
    activeParentId: activeDrag?.element.id,
    columnItemCounts,
    columnItemIds
  });

  const getIsDropDisabled = (columnId: string): boolean => {
    if (readOnly) return true;
    const fn = columnConfig.getIsDropDisabled;
    if (!fn) return false;
    return fn(columnId, buildDropDisabledContext());
  };

  const resolveEmptyMessage = (columnId: string): string | undefined => {
    const msg = columnConfig.emptyMessages?.[columnId];
    if (!msg) return undefined;
    if (typeof msg === 'function') return msg({ columnItemCounts });
    return msg;
  };

  const showLoader = isLoading || data.length === 0;

  return (
    <SelectionProvider
      value={{
        isSelected,
        toggleSelect,
        removeMany,
        addMany,
        selectionSize: selection.size,
        isMultiDragActive: isMultiDrag,
        isDark,
        isReadOnly: readOnly
      }}
    >
      <DndToolbar
        hasOpen={hasOpen}
        authorizedLength={authorizedLength}
        unauthorizedLength={unauthorizedLength}
        selectionSize={selection.size}
        onReset={onReset}
        onCloseAll={closeAll}
        onClearSelection={clearSelection}
        title={toolbarConfig?.title}
        resetTooltip={toolbarConfig?.resetTooltip}
        closeAllTooltip={toolbarConfig?.closeAllTooltip}
        deselectTooltip={toolbarConfig?.deselectTooltip}
        readOnly={readOnly}
      />
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ height, overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDrag(null)}
          onDragStart={(event) => {
            setActiveDrag(event.active.data.current as ActiveElement);
          }}
          autoScroll
        >
          <DragOverlay>
            {activeDrag ? (
              isMultiDrag ? (
                <MultiDragOverlay selection={selection} columns={columns} isOpen={isOpen} />
              ) : activeDrag.type === 'parent' ? (
                <DndItemCard
                  item={activeDrag.element}
                  columnId={activeDrag.columnId ?? 'source'}
                  isOpen={isOpen}
                  onToggleAccordion={() => undefined}
                />
              ) : (
                <DndChildCard
                  child={activeDrag.element.children[0]}
                  parent={activeDrag.element}
                  columnId={activeDrag.columnId ?? 'source'}
                />
              )
            ) : null}
          </DragOverlay>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              position: 'relative',
              flexGrow: 1,
              height: '100%'
            }}
          >
            {showLoader && <FullSizeLoader fullSizeSkeleton />}
            {columns.map((column) => (
              <DndColumnBox
                key={column.id}
                column={column}
                columns={columns}
                isOpen={isOpen}
                toggle={toggle}
                isDropDisabled={getIsDropDisabled(column.id)}
                emptyMessage={resolveEmptyMessage(column.id)}
              />
            ))}
          </Box>
        </DndContext>
      </Box>
    </SelectionProvider>
  );
};

export default DndSelector;
